import React, { useMemo } from 'react'
import {
  CaretDown,
  CaretLeft,
  CaretRight,
  DownloadSimpleIcon,
} from '@phosphor-icons/react'
import { SelectOption } from '../../types/SelectOption'
import { Role } from '../../types/Roles'
import { getWeekOfYear } from '../../utils/general'
import { downloadTribeOrgReport } from '../../utils/tribeOrgExportHelper'
import moment from 'moment'

interface Props {
  guildsInfo: Array<{ name: string; color: string; guildId: number }>
  guilds: Role[]
  tribesOrg: any[]
  startDate: string
  endDate: string
  selectedYear: SelectOption
  selectedWeek: SelectOption
  selectedSnapshot: SelectOption
  snapshotDates: SelectOption[]
  yearOptions: SelectOption[]
  setSelectedYear: (SelectOption) => void
  onYearChange: (SelectOption) => void
  setSelectedWeek: (SelectOption) => void
  setSelectedSnapshot: (SelectOption) => void
  setCurrent: () => void
  isBusy?: boolean
}

const TribeOrgHeaderContainer = ({
  guilds,
  tribesOrg,
  startDate,
  endDate,
  selectedYear,
  selectedWeek,
  snapshotDates,
  selectedSnapshot,
  yearOptions,
  setSelectedYear,
  onYearChange,
  setSelectedWeek,
  setSelectedSnapshot,
  setCurrent,
  isBusy = false,
}: Props) => {
  const filteredDates = useMemo(() => {
    return snapshotDates.filter(snapshotDate => {
      if (selectedYear.value === '') return false
      const date = moment(snapshotDate.value).utc()
      if (!date.isValid()) {
        return false
      }
      return date.isoWeekYear() === parseInt(selectedYear.value)
    })
  }, [snapshotDates, selectedYear.value])

  const selectWeeks = useMemo(() => {
    return filteredDates.map(snap => {
      const weekOfYear = moment(snap.value).utc().isoWeek()
      return { label: `Week ${weekOfYear}`, value: `${weekOfYear}` }
    })
  }, [filteredDates])

  const getWeeksForYear = (yearValue: string) => {
    return snapshotDates
      .filter(snapshotDate => {
        const date = moment(snapshotDate.value).utc()
        if (!date.isValid()) return false
        return date.isoWeekYear() === parseInt(yearValue)
      })
      .map(snap => {
        const weekOfYear = moment(snap.value).utc().isoWeek()
        return { label: `Week ${weekOfYear}`, value: `${weekOfYear}` }
      })
  }

  const backward = () => {
    if (isBusy || selectWeeks.length === 0 || filteredDates.length === 0) return
    const currentWeek = parseInt(selectedWeek.value)
    const limitWeek = parseInt(selectWeeks[selectWeeks.length - 1].value)
    const currentWeekIndex = selectWeeks.findIndex(
      week => parseInt(week.value) === currentWeek,
    )
    const yearIndex = yearOptions.findIndex(
      year => year.value === selectedYear.value,
    )

    if (
      currentWeek - 1 >= limitWeek &&
      currentWeekIndex < selectWeeks.length - 1
    ) {
      setSelectedWeek({
        label: `Week ${selectWeeks[currentWeekIndex + 1].value}`,
        value: `${selectWeeks[currentWeekIndex + 1].value}`,
      })
      return
    }

    if (yearIndex > 0 && currentWeek) {
      const lastYear = yearOptions[yearIndex - 1]
      const lastYearWeeks = getWeeksForYear(lastYear.value)
      if (lastYearWeeks.length > 0) {
        // Newest-first: first item is the latest week of that year
        setSelectedYear(lastYear)
        setSelectedWeek(lastYearWeeks[0])
        return
      }
    }

    const weekOfYear = getWeekOfYear(filteredDates[0].value)
    setSelectedSnapshot(filteredDates[0])
    setSelectedWeek({ value: `${weekOfYear}`, label: `Week ${weekOfYear}` })
  }

  const downloadReport = () => {
    if (isBusy) return
    downloadTribeOrgReport(tribesOrg, {
      guilds,
      isSnapshot: selectedSnapshot.value !== '',
      startDate,
      endDate,
      selectedSnapshotLabel: selectedSnapshot.value
        ? selectedSnapshot.label
        : undefined,
    })
  }

  const forward = () => {
    if (isBusy || selectWeeks.length === 0 || selectedWeek.value === '') return
    const currentWeek = parseInt(selectedWeek.value)
    const limitWeek = parseInt(selectWeeks[0].value)
    const currentWeekIndex = selectWeeks.findIndex(
      week => parseInt(week.value) === currentWeek,
    )
    const yearIndex = yearOptions.findIndex(
      year => year.value === selectedYear.value,
    )

    if (currentWeek + 1 <= limitWeek && currentWeekIndex > 0) {
      setSelectedWeek({
        label: `Week ${selectWeeks[currentWeekIndex - 1].value}`,
        value: `${selectWeeks[currentWeekIndex - 1].value}`,
      })
      return
    }

    if (yearIndex !== -1 && yearIndex < yearOptions.length - 1) {
      const nextYear = yearOptions[yearIndex + 1]
      const nextYearWeeks = getWeeksForYear(nextYear.value)
      if (nextYearWeeks.length > 0) {
        // Newest-first: last item is the earliest week of that year
        setSelectedYear(nextYear)
        setSelectedWeek(nextYearWeeks[nextYearWeeks.length - 1])
        return
      }
    }

    setCurrent()
  }

  const dateRangeLabel = selectedSnapshot.value
    ? selectedSnapshot.label
    : 'Current State'

  document.title = 'Time Off - Tribe Org'

  return (
    <div className="tribe-org__toolbar">
      <div className="tribe-org__filters-container">
        <div className="tribe-org__filters">
          <div className="tribe-org__filter-field">
            <span className="tribe-org__filter-label">Year</span>
            <div className="tribe-org__select-wrap">
              <select
                className="tribe-org__select tribe-org__select--year"
                value={selectedYear.value}
                disabled={isBusy}
                onChange={e => {
                  const option = yearOptions.find(
                    opt => opt.value === e.target.value,
                  )
                  if (option) {
                    onYearChange(option)
                  } else {
                    setSelectedYear({ value: '', label: '' })
                  }
                }}
              >
                {yearOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <CaretDown
                size={14}
                weight="regular"
                className="tribe-org__select-icon"
              />
            </div>
          </div>

          <div className="tribe-org__filter-field">
            <span className="tribe-org__filter-label">Week</span>
            <div className="tribe-org__select-wrap">
              <select
                className="tribe-org__select tribe-org__select--week"
                value={selectedWeek.value}
                disabled={isBusy}
                onChange={e => {
                  if (!e.target.value) return
                  const option = selectWeeks.find(
                    opt => opt.value === e.target.value,
                  )
                  if (option) {
                    setSelectedWeek(option)
                  }
                }}
              >
                <option value="" disabled={selectedWeek.value !== ''}>
                  Current Week
                </option>
                {selectWeeks.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <CaretDown
                size={14}
                weight="regular"
                className="tribe-org__select-icon"
              />
            </div>
          </div>

          <div className="tribe-org__filter-field">
            <span className="tribe-org__filter-label">Date Range</span>
            <div className="tribe-org__date-nav">
              <button
                type="button"
                className="tribe-org__date-nav-btn"
                disabled={isBusy}
                onClick={() => backward()}
                aria-label="Previous week"
              >
                <CaretLeft size={14} weight="regular" />
              </button>
              <div className="tribe-org__date-range">{dateRangeLabel}</div>
              <button
                type="button"
                disabled={isBusy || selectedWeek.value === ''}
                className={`tribe-org__date-nav-btn ${
                  isBusy || selectedWeek.value === ''
                    ? 'tribe-org__date-nav-btn--disabled'
                    : ''
                }`}
                onClick={() => forward()}
                aria-label="Next week"
              >
                <CaretRight size={14} weight="regular" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="tribe-org__actions">
        <button
          type="button"
          className="tribe-org__download-btn tribe-org__download-btn--secondary"
          disabled={isBusy}
          onClick={downloadReport}
        >
          <DownloadSimpleIcon size={16} weight="regular" color="#0A0A0A" />
          Download Tribe Org
        </button>

        <button
          type="button"
          className="tribe-org__current-state-btn"
          onClick={() => setCurrent()}
          disabled={isBusy || selectedSnapshot.value === ''}
        >
          Current State
        </button>
      </div>
    </div>
  )
}

export default TribeOrgHeaderContainer
